import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity,
  ArrowRight,
  Braces,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleDashed,
  CircleX,
  Clock3,
  Code2,
  Database,
  ExternalLink,
  FileUp,
  Film,
  Gauge,
  HardDrive,
  Image as ImageIcon,
  Info,
  Loader2,
  Play,
  RefreshCw,
  RotateCcw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Upload,
  X,
} from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ROUTES } from '@/app/routes';
import { http } from '@/api/http';
import { cn } from '@/lib/utils';
import { coopInvestigationUrl } from '@/lib/moderationToolLinks';
import {
  displayHarnessVerdictLabel,
  formatActionVerdictLabel,
  isNoActionLike,
  isTerminalCaseStatus,
  PROVISIONAL_CASE_STATUSES,
} from '@/features/moderation/lib/verdictDisplay';
import { ContentInfrastructureHealth } from '../components/ContentInfrastructureHealth';

type PostType = 'NORMAL' | 'IMAGES' | 'FLICKS' | 'EXCLUSIVE';
type FileType = 'IMAGE' | 'VIDEO';
type UploadStatus = 'idle' | 'uploading' | 'done' | 'error';
type ResultStatus = 'processing' | 'completed' | 'failed' | 'partial' | 'timeout';
type IntelligenceFetchStatus = 'complete' | 'processing' | 'empty' | 'error';

interface MediaDoc {
  fileName: string;
  fileType: FileType;
  width: number;
  height: number;
  videoDuration?: number | undefined;
  file?: File | undefined;
  previewUrl?: string | undefined;
  uploadProgress: number;
  uploadStatus: UploadStatus;
}

interface SignalResult {
  signal_id: string;
  display_name: string;
  channel: string;
  term: string;
  score: number;
  threshold: number;
}

interface RuleResult {
  rule_id: string;
  rule_name: string;
  description: string;
  condition_summary: string;
  matched: boolean;
  informational: boolean;
  match_reason: string | null;
  action_name: string;
  action_type: string;
  linked_policies: string[];
  signal_values: Record<string, number>;
}

interface PipelineStepResult {
  stage: string;
  title: string;
  status: string;
  duration_ms: number | null;
  description: string;
}

interface EnforcementResult {
  action: string;
  verdict: string;
  actor_id: string;
  reason: string;
  executed_at: string | null;
}

interface EnforcementPreview {
  action: string;
  verdict: string;
  actor_id: string;
  reason: string;
  would_execute: string[];
  blocked_by: string | null;
}

interface PipelineResult {
  postId?: number | undefined;
  caseId?: string | undefined;
  verdict?: string | undefined;
  verdictReason?: string | undefined;
  caseStatus?: ResultStatus | undefined;
  /** True when Coop case_status is a real terminal outcome (not mid-pipeline). */
  verdictFinal?: boolean | undefined;
  /** Raw case_status from Coop / media-manager (heuristic, standard, approved, …). */
  rawCaseStatus?: string | undefined;
  vectors?: Record<string, Record<string, number>> | undefined;
  signals?: SignalResult[] | undefined;
  rules?: RuleResult[] | undefined;
  enforcementActions?: EnforcementResult[] | undefined;
  enforcementPreviews?: EnforcementPreview[] | undefined;
  pipelineSteps?: PipelineStepResult[] | undefined;
  coopUrl?: string | undefined;
  rawPayload?: Record<string, unknown> | undefined;
  /** TipMi post_status after create/hold: HID, WAT, or PUB. */
  publicationStatus?: string | undefined;
  pipelineOnly?: boolean | undefined;
}

interface TestHistoryEntry {
  postId: number;
  fileName: string;
  fileType: FileType;
  fileSize: number;
  caption: string;
  userId: number;
  createdAt: string;
  verdict?: string | undefined;
  /** False while Coop may still change the verdict; missing + verdict ⇒ legacy final. */
  verdictFinal?: boolean | undefined;
  pipelineOnly: boolean;
  publicationStatus?: string | undefined;
}

const HISTORY_KEY = 'tipmi-moderation-test-history';
const MAX_POLL_ATTEMPTS = 30;
/** After fast poll exhausts, keep refreshing while Coop may still settle. */
const SOFT_REFRESH_MS = 15_000;
const panelClass =
  'rounded-xl border border-white/[0.09] bg-[#111114]/95 shadow-[0_12px_36px_rgba(0,0,0,0.2)]';

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isHistoryVerdictFinal(entry: TestHistoryEntry): boolean {
  if (!entry.verdict) return false;
  // Legacy rows written before verdictFinal existed are treated as final.
  return entry.verdictFinal !== false;
}

function loadHistory(): TestHistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as TestHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: TestHistoryEntry[]) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, 50)));
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function asRecords(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(asRecord) : [];
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : fallback;
}

function numberValue(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function nullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function normalizeIntelligence(payload: Record<string, unknown>): PipelineResult {
  const signals = asRecords(payload.signals).map((signal) => ({
    signal_id: stringValue(signal.signal_id ?? signal.signalId),
    display_name: stringValue(signal.display_name ?? signal.displayName, 'Unnamed signal'),
    channel: stringValue(signal.channel),
    term: stringValue(signal.term),
    score: numberValue(signal.score),
    threshold: numberValue(signal.threshold),
  }));
  const rules = asRecords(payload.rule_evaluations ?? payload.rules).map((rule) => {
    const actionType = stringValue(rule.action_type ?? rule.actionType);
    return {
      rule_id: stringValue(rule.rule_id ?? rule.ruleId),
      rule_name: stringValue(rule.rule_name ?? rule.ruleName, 'Unnamed rule'),
      description: stringValue(rule.description),
      condition_summary: stringValue(rule.condition_summary ?? rule.conditionSummary),
      matched: Boolean(rule.matched),
      informational:
        Boolean(rule.informational) || actionType === 'informational' || actionType === 'safety',
      match_reason: rule.match_reason == null ? null : stringValue(rule.match_reason),
      action_name: stringValue(rule.action_name ?? rule.actionName),
      action_type: actionType,
      linked_policies: Array.isArray(rule.linked_policies)
        ? rule.linked_policies.map((policy) => stringValue(policy)).filter(Boolean)
        : [],
      signal_values: Object.fromEntries(
        Object.entries(asRecord(rule.signal_values ?? rule.signalValues)).map(([key, value]) => [
          key,
          numberValue(value),
        ]),
      ),
    };
  });
  const pipelineSteps = asRecords(payload.pipeline_steps ?? payload.pipelineSteps).map((step) => ({
    stage: stringValue(step.stage, 'stage'),
    title: stringValue(step.title ?? step.stage, 'Pipeline stage'),
    status: stringValue(step.status, 'pending').toLowerCase(),
    duration_ms: nullableNumber(step.duration_ms ?? step.durationMs),
    description: stringValue(step.description),
  }));
  const enforcementActions = asRecords(
    payload.enforcement_actions ?? payload.enforcementActions,
  ).map((action) => ({
    action: stringValue(action.action, 'Unknown action'),
    verdict: stringValue(action.verdict),
    actor_id: stringValue(action.actor_id ?? action.actorId),
    reason: stringValue(action.reason),
    executed_at:
      action.executed_at == null && action.executedAt == null
        ? null
        : stringValue(action.executed_at ?? action.executedAt),
  }));
  const enforcementPreviews = asRecords(
    payload.enforcement_previews ?? payload.enforcementPreviews,
  ).map((preview) => {
    const wouldExecute = preview.would_execute ?? preview.wouldExecute;
    return {
      action: stringValue(preview.action, 'Unknown action'),
      verdict: stringValue(preview.verdict),
      actor_id: stringValue(preview.actor_id ?? preview.actorId),
      reason: stringValue(preview.reason),
      would_execute: Array.isArray(wouldExecute)
        ? wouldExecute.map((item: unknown) => stringValue(item)).filter(Boolean)
        : [],
      blocked_by:
        preview.blocked_by == null && preview.blockedBy == null
          ? null
          : stringValue(preview.blocked_by ?? preview.blockedBy),
    };
  });

  const rawVectors = asRecord(payload.vectors);
  const vectors = Object.fromEntries(
    Object.entries(rawVectors).map(([group, values]) => [
      group,
      Object.fromEntries(
        Object.entries(asRecord(values)).map(([key, value]) => [key, numberValue(value)]),
      ),
    ]),
  );

  const status = stringValue(payload.case_status ?? payload.caseStatus, 'processing').toLowerCase();
  const terminal = isTerminalCaseStatus(status);
  let caseStatus: ResultStatus;
  if (status === 'failed' || status === 'timeout') {
    caseStatus = status;
  } else if (status === 'partial' || PROVISIONAL_CASE_STATUSES.has(status)) {
    // Mid-pipeline snapshots (heuristic / standard / deep) — show results, keep polling.
    caseStatus = 'partial';
  } else if (terminal) {
    caseStatus = 'completed';
  } else {
    caseStatus = 'processing';
  }

  return {
    caseId: stringValue(payload.case_id ?? payload.caseId) || undefined,
    verdict: stringValue(payload.verdict) || undefined,
    verdictReason: stringValue(payload.verdict_reason ?? payload.verdictReason) || undefined,
    caseStatus,
    verdictFinal: terminal,
    rawCaseStatus: status || undefined,
    vectors: Object.keys(vectors).length > 0 ? vectors : undefined,
    signals,
    rules,
    enforcementActions,
    enforcementPreviews,
    pipelineSteps,
    coopUrl: stringValue(payload.coop_url ?? payload.coopUrl) || undefined,
    rawPayload: payload,
  };
}

function statusLabel(status: string | undefined, verdictFinal?: boolean): string {
  if (!status) return 'Waiting';
  if (status === 'partial' && verdictFinal === false) return 'Updating…';
  if (status === 'timeout' && verdictFinal === false) return 'Auto-refreshing';
  if (status === 'timeout') return 'Timed out';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function publicationStatusLabel(code: string | undefined, pipelineOnly?: boolean): string {
  const upper = (code ?? '').toUpperCase();
  if (upper === 'HID' || upper === 'HIDDEN') return 'HID · off feed';
  if (upper === 'WAT' || upper === 'WAITING') return 'WAT · held until approve';
  if (upper === 'PUB' || upper === 'PUBLISHED') return 'PUB · live';
  if (pipelineOnly === false) return 'Hold (WAT)';
  if (pipelineOnly === true) return 'Pipeline-only (HID)';
  return code ? upper : '—';
}

function statusClass(status: string | undefined): string {
  switch (status) {
    case 'completed':
      return 'border-lime-400/30 bg-lime-400/10 text-lime-300';
    case 'failed':
      return 'border-rose-400/30 bg-rose-400/10 text-rose-300';
    case 'timeout':
      return 'border-sky-400/30 bg-sky-400/10 text-sky-300';
    case 'partial':
      return 'border-amber-400/30 bg-amber-400/10 text-amber-300';
    case 'processing':
      return 'border-sky-400/30 bg-sky-400/10 text-sky-300';
    default:
      return 'border-white/10 bg-white/[0.05] text-muted-foreground';
  }
}

function verdictClass(verdict: string | undefined, provisional = false): string {
  if (provisional && isNoActionLike(verdict)) {
    return 'border-sky-400/30 bg-sky-400/10 text-sky-300';
  }
  const normalized = verdict?.toLowerCase() ?? '';
  if (normalized.includes('approve')) return 'border-lime-400/30 bg-lime-400/10 text-lime-300';
  if (normalized.includes('reject') || normalized.includes('block')) {
    return 'border-rose-400/30 bg-rose-400/10 text-rose-300';
  }
  if (normalized.includes('escalat')) return 'border-amber-400/30 bg-amber-400/10 text-amber-300';
  if (provisional) return 'border-amber-400/30 bg-amber-400/10 text-amber-300';
  return 'border-white/10 bg-white/[0.05] text-zinc-300';
}

function scoreColor(score: number, threshold: number): string {
  if (score >= threshold) return 'bg-rose-500';
  if (score >= threshold * 0.7) return 'bg-amber-400';
  return 'bg-lime-400';
}

/**
 * Moderation Pipeline Test Harness — submit content through the full moderation pipeline
 * and inspect the complete Coop-backed result, including signals, vectors, rules, actions,
 * stage timing, and the raw response payload.
 */
export function CreateTestPostPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState(searchParams.get('userId') ?? '904');
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState<PostType>('NORMAL');
  const [ratio, setRatio] = useState('9:16');
  const [createPost, setCreatePost] = useState(false);
  const [documents, setDocuments] = useState<MediaDoc[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<PipelineResult | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [history, setHistory] = useState<TestHistoryEntry[]>(loadHistory);
  const [loadingHistoryPostId, setLoadingHistoryPostId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** Bumped to cancel in-flight poll / soft-refresh loops. */
  const pollGenerationRef = useRef(0);

  function updateDoc(index: number, updates: Partial<MediaDoc>) {
    setDocuments((prev) => prev.map((doc, i) => (i === index ? { ...doc, ...updates } : doc)));
  }

  function removeDocument(index: number) {
    setDocuments((prev) => {
      const doc = prev[index];
      if (doc?.previewUrl) URL.revokeObjectURL(doc.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }

  function detectFileType(file: File): FileType {
    return file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE';
  }

  const addFiles = useCallback((files: FileList | File[]) => {
    const newDocs: MediaDoc[] = Array.from(files).map((file) => ({
      fileName: '',
      fileType: detectFileType(file),
      width: 1080,
      height: 1920,
      file,
      previewUrl: URL.createObjectURL(file),
      uploadProgress: 0,
      uploadStatus: 'idle',
    }));
    setDocuments((prev) => [...prev, ...newDocs]);
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragOver(false);
      if (event.dataTransfer.files.length > 0) addFiles(event.dataTransfer.files);
    },
    [addFiles],
  );

  async function uploadFile(index: number): Promise<string> {
    const doc = documents[index];
    if (!doc?.file) throw new Error('No file selected');
    const file = doc.file;
    updateDoc(index, { uploadStatus: 'uploading', uploadProgress: 0 });

    const params = new URLSearchParams({
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    });
    const urlRes = await http.post(`/admin/api/post-manager/upload-url?${params.toString()}`);
    const uploadData = asRecord(asRecord(urlRes.data).data);
    const url = stringValue(uploadData.url);
    const s3Key = stringValue(uploadData.s3Key);
    await axios.put(url, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      onUploadProgress: (event) => {
        if (event.total)
          updateDoc(index, { uploadProgress: Math.round((event.loaded / event.total) * 100) });
      },
    });
    updateDoc(index, { uploadStatus: 'done', uploadProgress: 100, fileName: s3Key });
    return s3Key;
  }

  async function fetchIntelligence(postId: number): Promise<IntelligenceFetchStatus> {
    try {
      const response = await http.get(`/admin/api/moderation/content/${postId}/intelligence`);
      const payload = asRecord(asRecord(response.data).data);
      if (Object.keys(payload).length === 0) return 'empty';

      const intelligence = normalizeIntelligence(payload);
      const isFinal = intelligence.verdictFinal === true;
      setResult((previous) => ({
        ...previous,
        postId,
        ...intelligence,
      }));

      if (intelligence.verdict) {
        setHistory((previous) => {
          const updated = previous.map((entry) =>
            entry.postId === postId
              ? {
                  ...entry,
                  verdict: intelligence.verdict,
                  verdictFinal: isFinal,
                }
              : entry,
          );
          saveHistory(updated);
          return updated;
        });
      }
      return isFinal ? 'complete' : 'processing';
    } catch {
      return 'error';
    }
  }

  async function fetchTranscription(postId: number) {
    try {
      const response = await http.get(`/admin/api/post-manager/${postId}/transcription`);
      const data = asRecord(asRecord(response.data).data);
      const transcription = stringValue(data.transcription);
      if (transcription) {
        setResult((previous) => ({
          ...previous,
          postId,
          verdictReason: previous?.verdictReason ?? `[Transcription] ${transcription}`,
          caseStatus: previous?.caseStatus === 'processing' ? 'partial' : previous?.caseStatus,
        }));
      }
    } catch {
      // Transcription is optional early feedback.
    }
  }

  async function softRefreshLoop(postId: number, generation: number, announce: boolean) {
    while (pollGenerationRef.current === generation) {
      await delay(SOFT_REFRESH_MS);
      if (pollGenerationRef.current !== generation) return;
      setLoadingHistoryPostId(postId);
      const status = await fetchIntelligence(postId);
      if (pollGenerationRef.current !== generation) return;
      if (status === 'complete') {
        setLoadingHistoryPostId(null);
        if (announce) toast.success(`Pipeline finished for #${postId}`);
        return;
      }
      if (status === 'empty' || status === 'error') {
        setLoadingHistoryPostId(null);
        continue;
      }
      setPollCount((count) => count + 1);
    }
  }

  /**
   * Fast poll (~3s) then soft refresh (~15s) until Coop reaches a terminal case_status.
   * Load / Retry / submit all share this loop so provisional NO_ACTION keeps updating.
   */
  async function runPollLoop(
    postId: number,
    generation: number,
    attempt: number,
    announce: boolean,
  ) {
    if (pollGenerationRef.current !== generation) return;

    setPollCount(attempt + 1);
    setLoadingHistoryPostId(postId);
    const status = await fetchIntelligence(postId);
    if (pollGenerationRef.current !== generation) return;

    if (status === 'complete') {
      setLoadingHistoryPostId(null);
      if (announce) toast.success(`Loaded final pipeline results for #${postId}`);
      return;
    }

    if (attempt === 0 && announce) {
      if (status === 'empty' || status === 'error') {
        toast.error(`No pipeline output is available for #${postId} yet.`);
      } else {
        toast.message(`Refreshing #${postId} — Coop is still processing.`);
      }
    }

    if (attempt === 5) void fetchTranscription(postId);

    if (attempt >= MAX_POLL_ATTEMPTS - 1) {
      await fetchTranscription(postId);
      if (pollGenerationRef.current !== generation) return;
      setResult((previous) => {
        if (previous?.verdictFinal) return previous;
        return {
          ...previous,
          postId,
          caseStatus: 'timeout',
          verdictFinal: false,
        };
      });
      setLoadingHistoryPostId(postId);
      if (announce) {
        toast.message(
          `Still waiting on Coop for #${postId} — auto-refreshing every ${SOFT_REFRESH_MS / 1000}s.`,
        );
      }
      await softRefreshLoop(postId, generation, announce);
      return;
    }

    await delay(3000);
    if (pollGenerationRef.current !== generation) return;
    await runPollLoop(postId, generation, attempt + 1, announce);
  }

  function startPolling(postId: number, options?: { announce?: boolean }) {
    const generation = ++pollGenerationRef.current;
    setPollCount(0);
    void runPollLoop(postId, generation, 0, options?.announce ?? false);
  }

  // Resume the most recent provisional history row on page load (same as clicking Load).
  useEffect(() => {
    const provisional = history.find(
      (entry) => entry.verdict && entry.verdictFinal === false,
    );
    if (provisional) startPolling(provisional.postId);
    return () => {
      pollGenerationRef.current += 1;
    };
    // Intentionally once on mount for the freshest provisional row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit() {
    if (!userId.trim()) {
      toast.error('User ID is required.');
      return;
    }
    if (documents.length === 0) {
      toast.error('At least one media file is required.');
      return;
    }

    setIsSubmitting(true);
    setResult(null);
    setPollCount(0);
    try {
      const resolvedDocs: {
        fileName: string;
        fileType: string;
        width: number;
        height: number;
        videoDuration?: number | undefined;
      }[] = [];

      for (let i = 0; i < documents.length; i += 1) {
        const doc = documents[i];
        if (!doc) continue;
        let s3Key = doc.fileName.trim();
        if (doc.file && doc.uploadStatus !== 'done') s3Key = await uploadFile(i);
        if (s3Key) {
          resolvedDocs.push({
            fileName: s3Key,
            fileType: doc.fileType === 'VIDEO' ? 'VID' : 'IMG',
            width: doc.width,
            height: doc.height,
            videoDuration: doc.fileType === 'VIDEO' ? (doc.videoDuration ?? 10) : undefined,
          });
        }
      }

      if (resolvedDocs.length === 0) {
        toast.error('No media files to submit.');
        return;
      }

      const response = await http.post('/admin/api/post-manager/create-as-user', {
        userId: Number(userId),
        content,
        postType,
        ratio,
        documents: resolvedDocs,
        pipelineOnly: !createPost,
      });
      const responseData = asRecord(asRecord(response.data).data);
      const postId = numberValue(responseData.postId, 0) || undefined;
      const publicationStatus = stringValue(responseData.status) || undefined;
      const pipelineOnly = Boolean(responseData.pipelineOnly ?? !createPost);
      setResult({
        postId,
        caseStatus: 'processing',
        publicationStatus,
        pipelineOnly,
      });
      toast.success(
        publicationStatus
          ? `Content submitted (${publicationStatusLabel(publicationStatus, pipelineOnly)}) — pipeline is processing.`
          : 'Content submitted — pipeline is processing.',
      );

      if (postId) {
        const firstDoc = documents[0];
        const entry: TestHistoryEntry = {
          postId,
          fileName: firstDoc?.file?.name ?? firstDoc?.fileName ?? 'unknown',
          fileType: firstDoc?.fileType ?? 'VIDEO',
          fileSize: firstDoc?.file?.size ?? 0,
          caption: content.slice(0, 100),
          userId: Number(userId),
          createdAt: new Date().toISOString(),
          pipelineOnly,
          publicationStatus,
        };
        const updated = [entry, ...history];
        setHistory(updated);
        saveHistory(updated);
        setTimeout(() => startPolling(postId), 5000);
      }
    } catch (error) {
      const axiosError = error as import('axios').AxiosError;
      toast.error('Failed to submit content.');
      console.error('Submit error:', axiosError.response?.data ?? error);
      setDocuments((previous) =>
        previous.map((doc) =>
          doc.uploadStatus === 'uploading' ? { ...doc, uploadStatus: 'error' } : doc,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function loadHistoryResult(postId: number) {
    // Same path as Retry polling — keep refreshing until Coop reaches a terminal status.
    startPolling(postId, { announce: true });
  }

  const retryResult = () => {
    if (result?.postId) startPolling(result.postId, { announce: true });
  };

  return (
    <div className="min-h-full bg-[#0b0b0d] px-4 py-4 text-zinc-100 sm:px-6 lg:px-8">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-lg border border-violet-400/30 bg-violet-500/10 p-2.5 text-violet-400">
            <Shield className="size-5" aria-hidden />
          </div>
          <div>
            <nav className="mb-1 flex items-center gap-1 text-[11px] text-muted-foreground">
              <Link to={ROUTES.pipelineTestPosts} className="hover:text-violet-300">
                Pipeline Tests
              </Link>
              <ChevronRight className="size-3" />
              <span className="text-muted-foreground">New test</span>
            </nav>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[1.45rem] font-semibold tracking-tight text-white">
                Pipeline Test
              </h1>
              <span className="rounded-full border border-violet-400/25 bg-violet-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-300">
                Coop AI
              </span>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Same pre-publish hold as mobile: create → WAT/HID → Coop case → verdict. Pipeline-only
              stays off the feed even if Coop approves.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ContentInfrastructureHealth />
          <Button
            type="button"
            variant="outline"
            className="h-9 border-white/10 bg-[#111114] px-3 text-xs text-zinc-300 shadow-none hover:bg-white/[0.08] hover:text-white"
            onClick={() => {
              pollGenerationRef.current += 1;
              setLoadingHistoryPostId(null);
              setPollCount(0);
              setResult(null);
            }}
          >
            <RotateCcw className="mr-2 size-3.5" /> Reset
          </Button>
        </div>
      </header>

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard
          label="Post status"
          value={result ? publicationStatusLabel(result.publicationStatus, result.pipelineOnly) : '—'}
          icon={<Shield />}
          accent="amber"
        />
        <MetricCard
          label="Signals returned"
          value={result?.signals?.length ?? 0}
          icon={<Gauge />}
          accent="blue"
        />
        <MetricCard
          label="Rules evaluated"
          value={result?.rules?.length ?? 0}
          icon={<CircleAlert />}
          accent="amber"
        />
        <MetricCard
          label="Pipeline stages"
          value={result?.pipelineSteps?.length ?? 0}
          icon={<Database />}
          accent="green"
        />
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(360px,0.84fr)_minmax(0,1.6fr)]">
        <div className="space-y-4">
          <TestInputPanel
            documents={documents}
            fileInputRef={fileInputRef}
            isDragOver={isDragOver}
            isSubmitting={isSubmitting}
            userId={userId}
            content={content}
            postType={postType}
            ratio={ratio}
            createPost={createPost}
            onDrop={onDrop}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onBrowse={() => fileInputRef.current?.click()}
            onFiles={(files) => addFiles(files)}
            onRemove={removeDocument}
            onUserIdChange={setUserId}
            onContentChange={setContent}
            onPostTypeChange={setPostType}
            onRatioChange={setRatio}
            onCreatePostChange={setCreatePost}
            onSubmit={() => void onSubmit()}
          />
          <PipelineInfoPanel />
          <HistoryPanel
            history={history}
            onClear={() => {
              setHistory([]);
              localStorage.removeItem(HISTORY_KEY);
            }}
            onOpen={(postId) => void navigate(ROUTES.contentDetails(String(postId)))}
            onLoad={(postId) => loadHistoryResult(postId)}
            loadingPostId={loadingHistoryPostId}
            onRetry={async (postId) => {
              try {
                await http.post('/admin/api/moderation/replay-posts', [postId]);
                toast.success(`Pipeline triggered for #${postId}`);
              } catch {
                toast.error('Failed to trigger pipeline');
              }
            }}
          />
        </div>

        <PipelineOutputPanel
          result={result}
          isSubmitting={isSubmitting}
          pollCount={pollCount}
          onRetry={retryResult}
          onOpenContent={(postId) => void navigate(ROUTES.contentDetails(String(postId)))}
        />
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number | string;
  icon: ReactNode;
  accent: 'purple' | 'blue' | 'amber' | 'green';
}) {
  const styles = {
    purple: { icon: 'text-violet-400', glow: 'bg-violet-500/10', value: 'text-violet-100' },
    blue: { icon: 'text-sky-400', glow: 'bg-sky-500/10', value: 'text-sky-100' },
    amber: { icon: 'text-amber-400', glow: 'bg-amber-500/10', value: 'text-amber-100' },
    green: { icon: 'text-lime-400', glow: 'bg-lime-500/10', value: 'text-lime-100' },
  }[accent];
  return (
    <div className={cn(panelClass, 'min-w-0 p-3.5')}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className={cn('rounded-md p-1.5', styles.glow, styles.icon)}>{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className={cn('mt-2 truncate text-xl font-semibold tracking-tight', styles.value)}>
        {value}
      </div>
      <p className="mt-1 truncate text-[10px] text-muted-foreground">Live result from this test run</p>
    </div>
  );
}

function TestInputPanel({
  documents,
  fileInputRef,
  isDragOver,
  isSubmitting,
  userId,
  content,
  postType,
  ratio,
  createPost,
  onDrop,
  onDragOver,
  onDragLeave,
  onBrowse,
  onFiles,
  onRemove,
  onUserIdChange,
  onContentChange,
  onPostTypeChange,
  onRatioChange,
  onCreatePostChange,
  onSubmit,
}: {
  documents: MediaDoc[];
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  isDragOver: boolean;
  isSubmitting: boolean;
  userId: string;
  content: string;
  postType: PostType;
  ratio: string;
  createPost: boolean;
  onDrop: (event: React.DragEvent) => void;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: () => void;
  onBrowse: () => void;
  onFiles: (files: FileList) => void;
  onRemove: (index: number) => void;
  onUserIdChange: (value: string) => void;
  onContentChange: (value: string) => void;
  onPostTypeChange: (value: PostType) => void;
  onRatioChange: (value: string) => void;
  onCreatePostChange: (value: boolean) => void;
  onSubmit: () => void;
}) {
  return (
    <section className={cn(panelClass, 'overflow-hidden')}>
      <PanelHeader
        icon={<Upload className="size-4" />}
        title="Test input"
        detail="Prepare content for analysis"
      />
      <div className="space-y-4 p-4 sm:p-5">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(event) => {
            if (event.target.files) onFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={onBrowse}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={cn(
            'flex min-h-[145px] w-full flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-5 text-center transition-colors',
            isDragOver
              ? 'border-violet-400 bg-violet-500/10'
              : 'border-white/[0.14] bg-white/[0.015] hover:border-violet-400/50 hover:bg-violet-500/[0.04]',
          )}
        >
          <span
            className={cn(
              'rounded-xl p-3',
              isDragOver ? 'bg-violet-500/20 text-violet-300' : 'bg-white/[0.06] text-muted-foreground',
            )}
          >
            <FileUp className="size-6" />
          </span>
          <span>
            <span className="block text-sm font-medium text-zinc-200">
              {isDragOver ? 'Drop files here' : 'Drag & drop media files'}
            </span>
            <span className="mt-1 block text-xs text-muted-foreground">
              Images and videos · click to browse
            </span>
          </span>
        </button>

        {documents.length > 0 && (
          <div className="space-y-2">
            {documents.map((doc, index) => (
              <MediaRow
                key={`${doc.fileName}-${index}`}
                doc={doc}
                onRemove={() => onRemove(index)}
              />
            ))}
          </div>
        )}

        <div className="border-t border-white/[0.08] pt-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-white">Test parameters</h2>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                These values are sent with the test post
              </p>
            </div>
            <Code2 className="size-4 text-muted-foreground" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="User ID">
              <Input
                value={userId}
                onChange={(event) => onUserIdChange(event.target.value)}
                type="number"
                className="h-9 border-white/10 bg-white/[0.04] text-xs text-zinc-200 placeholder:text-muted-foreground"
              />
            </Field>
            <Field label="Post type">
              <DarkSelect
                value={postType}
                onChange={(value) => onPostTypeChange(value as PostType)}
                options={['NORMAL', 'IMAGES', 'FLICKS', 'EXCLUSIVE']}
              />
            </Field>
            <Field label="Ratio">
              <DarkSelect
                value={ratio}
                onChange={onRatioChange}
                options={['9:16', '16:9', '1:1']}
              />
            </Field>
          </div>
          <Field label="Caption · text moderation">
            <textarea
              value={content}
              onChange={(event) => onContentChange(event.target.value)}
              placeholder="Optional caption to test CoPE text classification..."
              className="flex min-h-[72px] w-full resize-y rounded-md border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-zinc-200 outline-none placeholder:text-muted-foreground focus:border-violet-400/60"
            />
          </Field>
          <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
            <input
              type="checkbox"
              checked={createPost}
              onChange={(event) => onCreatePostChange(event.target.checked)}
              className="mt-0.5 size-3.5 accent-violet-500"
            />
            <span className="text-xs text-muted-foreground">
              <span className="font-medium text-zinc-200">Create visible post</span>
              <span className="text-muted-foreground">
                {' '}
                — off by default (HID, pipeline-only, never on the feed). On = same hold as mobile
                (WAT); the post goes live only after Coop or admin approve. Use a dedicated QA
                account.
              </span>
            </span>
          </label>
        </div>

        <Button
          onClick={onSubmit}
          disabled={isSubmitting || documents.length === 0}
          className="h-11 w-full border-0 bg-violet-600 text-sm text-white shadow-[0_0_20px_rgba(124,58,237,0.2)] hover:bg-violet-500 disabled:bg-violet-600/40"
        >
          {isSubmitting ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <Play className="mr-2 size-4 fill-current" />
          )}
          {isSubmitting ? 'Submitting to pipeline…' : 'Run through pipeline'}
        </Button>
      </div>
    </section>
  );
}

function MediaRow({ doc, onRemove }: { doc: MediaDoc; onRemove: () => void }) {
  return (
    <div className="flex gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] p-2.5">
      <div className="relative size-14 shrink-0 overflow-hidden rounded bg-white/[0.06]">
        {doc.previewUrl && doc.fileType === 'VIDEO' ? (
          <video src={doc.previewUrl} className="size-full object-cover" muted playsInline />
        ) : doc.previewUrl ? (
          <img src={doc.previewUrl} alt="" className="size-full object-cover" />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground">
            <ImageIcon className="size-5" />
          </div>
        )}
        {doc.fileType === 'VIDEO' && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/25">
            <Play className="size-4 fill-white text-white" />
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-xs font-medium text-zinc-300">
              {doc.file?.name ?? doc.fileName}
            </p>
            <p className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1">
                {doc.fileType === 'VIDEO' ? (
                  <Film className="size-3" />
                ) : (
                  <ImageIcon className="size-3" />
                )}
                {doc.fileType}
              </span>
              {doc.file && (
                <span className="flex items-center gap-1">
                  <HardDrive className="size-3" />
                  {formatFileSize(doc.file.size)}
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onRemove}
            disabled={doc.uploadStatus === 'uploading'}
            className="rounded p-1 text-muted-foreground hover:bg-white/[0.06] hover:text-zinc-200 disabled:opacity-40"
          >
            <X className="size-3.5" />
          </button>
        </div>
        {doc.uploadStatus === 'uploading' && (
          <div className="mt-2">
            <div className="mb-1 flex justify-between text-[10px] text-muted-foreground">
              <span>Uploading…</span>
              <span>{doc.uploadProgress}%</span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-white/[0.08]">
              <div
                className="h-full rounded-full bg-violet-500 transition-all"
                style={{ width: `${doc.uploadProgress}%` }}
              />
            </div>
          </div>
        )}
        {doc.uploadStatus === 'done' && (
          <p className="mt-1 flex items-center gap-1 text-[10px] text-lime-300">
            <CheckCircle2 className="size-3" /> Uploaded
          </p>
        )}
        {doc.uploadStatus === 'error' && (
          <p className="mt-1 text-[10px] text-rose-300">Upload failed</p>
        )}
      </div>
    </div>
  );
}

function PipelineInfoPanel() {
  return (
    <section className="rounded-xl border border-violet-400/15 bg-violet-500/[0.06] p-4 text-xs text-muted-foreground">
      <div className="flex items-start gap-3">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-sky-400/40 text-sky-300">
          <Info className="size-3.5" />
        </span>
        <div>
          <p className="font-medium text-zinc-200">Same hold path as mobile</p>
          <p className="mt-1 leading-relaxed">
            Create opens a Coop case under hold (no publish-first replay). CoPE, Gemma vision,
            Parakeet, signals, rules, and enforcement then run. Pipeline-only stays HID; a visible
            test can move WAT → PUB only after approve.
          </p>
        </div>
      </div>
    </section>
  );
}

function PipelineOutputPanel({
  result,
  isSubmitting,
  pollCount,
  onRetry,
  onOpenContent,
}: {
  result: PipelineResult | null;
  isSubmitting: boolean;
  pollCount: number;
  onRetry: () => void;
  onOpenContent: (postId: number) => void;
}) {
  const isProcessing = isSubmitting || result?.caseStatus === 'processing';
  const showDetailedOutput =
    !!result &&
    (result.caseStatus === 'completed' ||
      result.caseStatus === 'partial' ||
      result.caseStatus === 'failed' ||
      result.caseStatus === 'timeout' ||
      !!result.verdict);
  const stillUpdating =
    !!result &&
    result.verdictFinal !== true &&
    result.caseStatus !== 'failed' &&
    (pollCount > 0 || result.caseStatus === 'partial' || result.caseStatus === 'timeout');

  return (
    <section className={cn(panelClass, 'min-h-[560px] overflow-hidden')}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.08] px-4 py-3 sm:px-5">
        <div className="flex items-center gap-2">
          <span className="text-violet-400">
            <Activity className="size-4" />
          </span>
          <div>
            <h2 className="text-sm font-semibold text-white">Detailed pipeline output</h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Every stage and decision returned by Coop
            </p>
          </div>
        </div>
        {result?.caseId && (
          <span className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 font-mono text-[10px] text-muted-foreground">
            case {result.caseId}
          </span>
        )}
      </div>

      {!result && !isSubmitting && <EmptyOutput />}
      {isProcessing && !showDetailedOutput && (
        <ProcessingOutput
          postId={result?.postId}
          publicationStatus={result?.publicationStatus}
          pipelineOnly={result?.pipelineOnly}
          pollCount={pollCount}
          onOpenContent={onOpenContent}
        />
      )}
      {showDetailedOutput && result && (
        <div className="space-y-4 p-4 sm:p-5">
          {stillUpdating && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-sky-400/20 bg-sky-400/[0.06] px-3 py-2 text-[11px] text-sky-200">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="size-3.5 animate-spin" />
                {result.caseStatus === 'timeout'
                  ? `Auto-refreshing every ${SOFT_REFRESH_MS / 1000}s`
                  : 'Coop may still update this result'}
                {result.rawCaseStatus ? ` (${result.rawCaseStatus})` : ''}
              </span>
              {pollCount > 0 && (
                <span className="text-sky-300/70">
                  {result.caseStatus === 'timeout'
                    ? `Soft refresh #${pollCount}`
                    : `Polling ${pollCount}/${MAX_POLL_ATTEMPTS}`}
                </span>
              )}
            </div>
          )}
          <ResultSummary result={result} onRetry={onRetry} onOpenContent={onOpenContent} />
          {result.pipelineSteps && result.pipelineSteps.length > 0 && (
            <PipelineStages steps={result.pipelineSteps} />
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            <SignalsPanel signals={result.signals ?? []} />
            <VectorsPanel vectors={result.vectors} />
          </div>
          <RulesPanel rules={result.rules ?? []} />
          <ActionsPanel
            actions={result.enforcementActions ?? []}
            previews={result.enforcementPreviews ?? []}
          />
          <RawPayloadPanel payload={result.rawPayload} />
        </div>
      )}
    </section>
  );
}

function EmptyOutput() {
  return (
    <div className="flex min-h-[490px] flex-col items-center justify-center px-6 text-center">
      <div className="mb-4 rounded-2xl border border-violet-400/20 bg-violet-500/[0.08] p-4 text-violet-300">
        <Braces className="size-8" />
      </div>
      <h3 className="text-sm font-medium text-zinc-300">Waiting for a pipeline run</h3>
      <p className="mt-2 max-w-sm text-xs leading-relaxed text-muted-foreground">
        Submit an image or video to populate the live verdict, pipeline timings, signal scores, rule
        trace, actions, and raw Coop response.
      </p>
    </div>
  );
}

function ProcessingOutput({
  postId,
  publicationStatus,
  pipelineOnly,
  pollCount,
  onOpenContent,
}: {
  postId: number | undefined;
  publicationStatus?: string | undefined;
  pipelineOnly?: boolean | undefined;
  pollCount: number;
  onOpenContent: (postId: number) => void;
}) {
  const steps = [
    'Content accepted',
    'Media analysis',
    'Signal scoring',
    'Rule evaluation',
    'Decision',
  ];
  const active = Math.min(Math.floor(pollCount / 3), steps.length - 1);
  return (
    <div className="p-4 sm:p-5">
      <div className="rounded-xl border border-sky-400/15 bg-sky-400/[0.05] p-4">
        <div className="flex items-center gap-3">
          <Loader2 className="size-5 animate-spin text-sky-300" />
          <div>
            <p className="text-sm font-medium text-sky-100">Coop is processing this content</p>
            <p className="mt-0.5 text-xs text-sky-200/50">
              {publicationStatusLabel(publicationStatus, pipelineOnly)}. The result panel will
              update as intelligence becomes available.
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-2 sm:grid-cols-5">
          {steps.map((step, index) => (
            <div
              key={step}
              className={cn(
                'rounded-lg border px-2 py-2 text-center text-[10px]',
                index < active
                  ? 'border-lime-400/20 bg-lime-400/[0.06] text-lime-300'
                  : index === active
                    ? 'border-sky-400/30 bg-sky-400/10 text-sky-200'
                    : 'border-white/[0.08] bg-white/[0.02] text-muted-foreground',
              )}
            >
              <span className="mb-1 block">
                {index < active ? '✓' : index === active ? '•••' : '—'}
              </span>
              {step}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>Polling attempt {pollCount}/{MAX_POLL_ATTEMPTS}</span>
        {postId && (
          <button
            type="button"
            onClick={() => onOpenContent(postId)}
            className="text-violet-300 hover:text-violet-200"
          >
            Open content #{postId}
            <ArrowRight className="ml-1 inline size-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function ResultSummary({
  result,
  onRetry,
  onOpenContent,
}: {
  result: PipelineResult;
  onRetry: () => void;
  onOpenContent: (postId: number) => void;
}) {
  const terminalFailure = result.caseStatus === 'timeout' || result.caseStatus === 'failed';
  const isProvisional = result.verdictFinal !== true && !terminalFailure;
  const timedOutProvisional = result.caseStatus === 'timeout' && result.verdictFinal !== true;
  const verdictHeading = timedOutProvisional
    ? 'Last known snapshot'
    : isProvisional
      ? 'Provisional — not final'
      : 'Final verdict';
  const openInCoopHref = coopInvestigationUrl(result.caseId, result.coopUrl);
  const shownVerdict = displayHarnessVerdictLabel(result.verdict, result.verdictFinal === true);

  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_180px]">
        <div
          className={cn(
            'rounded-xl border p-4',
            terminalFailure && !timedOutProvisional
              ? 'border-rose-400/20 bg-rose-400/[0.05]'
              : isProvisional || timedOutProvisional
                ? 'border-amber-400/20 bg-amber-400/[0.05]'
                : 'border-violet-400/20 bg-violet-500/[0.06]',
          )}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  'flex size-10 items-center justify-center rounded-xl',
                  terminalFailure && !timedOutProvisional
                    ? 'bg-rose-500/15 text-rose-300'
                    : isProvisional || timedOutProvisional
                      ? 'bg-amber-500/15 text-amber-300'
                      : 'bg-violet-500/15 text-violet-300',
                )}
              >
                {terminalFailure && !timedOutProvisional ? (
                  <ShieldAlert className="size-5" />
                ) : result.verdictFinal === true &&
                  result.verdict?.toLowerCase().includes('approve') ? (
                  <ShieldCheck className="size-5" />
                ) : isProvisional || timedOutProvisional ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <Shield className="size-5" />
                )}
              </span>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  {verdictHeading}
                </p>
                <p className="mt-1 text-xl font-semibold text-white">
                  {shownVerdict}
                </p>
                {(isProvisional || timedOutProvisional) &&
                  result.verdict &&
                  !isNoActionLike(result.verdict) && (
                    <p className="mt-1 font-mono text-[10px] text-muted-foreground">
                      Coop: {result.verdict}
                    </p>
                  )}
                {(isProvisional || timedOutProvisional) && isNoActionLike(result.verdict) && (
                  <p className="mt-1 font-mono text-[10px] text-muted-foreground">
                    Coop snapshot: {result.verdict} (not a final decision)
                  </p>
                )}
              </div>
            </div>
            <StatusChip
              label={statusLabel(result.caseStatus, result.verdictFinal)}
              className={statusClass(result.caseStatus)}
            />
          </div>
          {isProvisional && (
            <p className="mt-4 border-t border-white/[0.08] pt-3 text-xs leading-relaxed text-amber-200/80">
              Coop may still change this result — auto-refreshing.
            </p>
          )}
          {timedOutProvisional && (
            <p className="mt-4 border-t border-white/[0.08] pt-3 text-xs leading-relaxed text-sky-200/80">
              Fast poll ended — still open in Coop. Auto-refreshing every {SOFT_REFRESH_MS / 1000}s
              (Load resumes the same loop).
            </p>
          )}
          {result.verdictReason && (
            <p
              className={cn(
                'text-xs leading-relaxed text-muted-foreground',
                isProvisional || timedOutProvisional
                  ? 'mt-2'
                  : 'mt-4 border-t border-white/[0.08] pt-3',
              )}
            >
              {result.verdictReason}
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
          <MiniStat label="Signals" value={result.signals?.length ?? 0} />
          <MiniStat
            label="Rules matched"
            value={result.rules?.filter((rule) => rule.matched).length ?? 0}
          />
          <MiniStat label="Actions" value={result.enforcementActions?.length ?? 0} />
          <MiniStat label="Proposed" value={result.enforcementPreviews?.length ?? 0} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {(result.publicationStatus || result.pipelineOnly !== undefined) && (
          <span className="inline-flex h-8 items-center rounded-md border border-amber-400/20 bg-amber-400/[0.06] px-3 text-xs text-amber-200">
            {publicationStatusLabel(result.publicationStatus, result.pipelineOnly)}
          </span>
        )}
        {result.postId && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenContent(result.postId!)}
            className="h-8 border-white/10 bg-white/[0.03] text-xs text-zinc-300 hover:bg-white/[0.08] hover:text-white"
          >
            View content #{result.postId}
          </Button>
        )}
        {openInCoopHref && (
          <a
            href={openInCoopHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-8 items-center rounded-md border border-white/10 bg-white/[0.03] px-3 text-xs text-zinc-300 hover:bg-white/[0.08] hover:text-white"
          >
            Open in Coop <ExternalLink className="ml-2 size-3" />
          </a>
        )}
        {(terminalFailure || !result.verdict || isProvisional) && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="h-8 border-amber-400/20 bg-amber-400/[0.04] text-xs text-amber-300 hover:bg-amber-400/10"
          >
            <RefreshCw className="mr-2 size-3" /> Retry polling
          </Button>
        )}
      </div>
    </div>
  );
}

function PipelineStages({ steps }: { steps: PipelineStepResult[] }) {
  return (
    <OutputSection
      title="Pipeline execution"
      detail={`${steps.length} stages reported`}
      icon={<Activity className="size-4" />}
    >
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {steps.map((step, index) => (
          <div
            key={`${step.stage}-${index}`}
            className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3"
          >
            <div className="flex items-start gap-2">
              <StageIcon status={step.status} />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="truncate text-xs font-medium text-zinc-200">{step.title}</p>
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                    {formatDuration(step.duration_ms)}
                  </span>
                </div>
                <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {step.stage} · {statusLabel(step.status)}
                </p>
                {step.description && (
                  <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </OutputSection>
  );
}

function SignalsPanel({ signals }: { signals: SignalResult[] }) {
  return (
    <OutputSection
      title="Signal scores"
      detail={`${signals.length} signals emitted`}
      icon={<Gauge className="size-4" />}
    >
      <div className="space-y-3">
        {signals.length === 0 ? (
          <EmptyDetail label="No signal scores returned." />
        ) : (
          signals.map((signal) => {
            const score = Math.min(Math.max(signal.score, 0), 1);
            const threshold = Math.min(Math.max(signal.threshold, 0), 1);
            return (
              <div key={signal.signal_id}>
                <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate text-zinc-300">{signal.display_name}</span>
                  <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                    {score.toFixed(3)}{' '}
                    <span className="text-muted-foreground">/ {threshold.toFixed(3)}</span>
                  </span>
                </div>
                <div className="relative h-2 overflow-hidden rounded-full bg-white/[0.07]">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      scoreColor(score, threshold),
                    )}
                    style={{ width: `${score * 100}%` }}
                  />
                  <span
                    className="absolute top-0 h-full w-px bg-white/60"
                    style={{ left: `${threshold * 100}%` }}
                  />
                </div>
                <div className="mt-1 flex gap-2 text-[10px] text-muted-foreground">
                  {signal.channel && <span>{signal.channel}</span>}
                  {signal.term && <span className="truncate">{signal.term}</span>}
                  <span className="ml-auto">threshold marker</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </OutputSection>
  );
}

function VectorsPanel({
  vectors,
}: {
  vectors: Record<string, Record<string, number>> | undefined;
}) {
  const entries = Object.entries(vectors ?? {}).flatMap(([group, values]) =>
    Object.entries(values).map(([label, value]) => ({ group, label, value })),
  );
  return (
    <OutputSection
      title="Model vectors"
      detail={`${entries.length} dimensions returned`}
      icon={<Braces className="size-4" />}
    >
      <div className="max-h-64 overflow-auto rounded-lg border border-white/[0.08] bg-[#0d0d10]">
        <table className="w-full text-left text-[11px]">
          <thead className="border-b border-white/[0.08] text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Vector group</th>
              <th className="px-3 py-2 font-medium">Dimension</th>
              <th className="px-3 py-2 text-right font-medium">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.05]">
            {entries.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-3 py-5 text-center text-muted-foreground">
                  No model vectors returned.
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr key={`${entry.group}-${entry.label}`}>
                  <td className="px-3 py-2 text-muted-foreground">{entry.group}</td>
                  <td className="px-3 py-2 font-mono text-zinc-300">{entry.label}</td>
                  <td className="px-3 py-2 text-right font-mono text-violet-300">
                    {entry.value.toFixed(4)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </OutputSection>
  );
}

function isInformationalRule(rule: RuleResult): boolean {
  return (
    rule.informational || rule.action_type === 'informational' || rule.action_type === 'safety'
  );
}

function RulesPanel({ rules }: { rules: RuleResult[] }) {
  const matchedEnforcementRules = rules.filter(
    (rule) => !isInformationalRule(rule) && rule.matched,
  ).length;
  return (
    <OutputSection
      title="Rule evaluation trace"
      detail={`${matchedEnforcementRules} matched · ${rules.length} evaluated`}
      icon={<CircleAlert className="size-4" />}
    >
      <div className="overflow-hidden rounded-lg border border-white/[0.08]">
        <div className="hidden grid-cols-[minmax(0,1.5fr)_100px_120px_100px] gap-3 border-b border-white/[0.08] bg-white/[0.025] px-3 py-2 text-[10px] uppercase tracking-wide text-muted-foreground sm:grid">
          <span>Rule</span>
          <span>Result</span>
          <span>Action</span>
          <span>Signals</span>
        </div>
        {rules.length === 0 ? (
          <EmptyDetail label="No rule evaluations returned." />
        ) : (
          <div className="divide-y divide-white/[0.06]">
            {rules.map((rule, index) => (
              <RuleRow key={`${rule.rule_id}-${index}`} rule={rule} />
            ))}
          </div>
        )}
      </div>
    </OutputSection>
  );
}

function RuleRow({ rule }: { rule: RuleResult }) {
  const [open, setOpen] = useState(false);
  const informational = isInformationalRule(rule);
  const matched = !informational && rule.matched;
  return (
    <div
      className={cn(
        'border-l-2',
        matched ? 'border-l-rose-400 bg-rose-400/[0.025]' : 'border-l-transparent',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="grid w-full grid-cols-1 gap-2 px-3 py-2.5 text-left sm:grid-cols-[minmax(0,1.5fr)_100px_120px_100px] sm:items-center sm:gap-3"
      >
        <span className="flex min-w-0 items-center gap-2 text-xs text-zinc-300">
          <span
            className={cn(
              'flex size-5 shrink-0 items-center justify-center rounded-full',
              informational
                ? 'bg-sky-500/10 text-sky-300'
                : matched
                  ? 'bg-rose-500/15 text-rose-300'
                  : 'bg-lime-500/10 text-lime-300',
            )}
          >
            {informational ? (
              <Info className="size-3" />
            ) : matched ? (
              <X className="size-3" />
            ) : (
              <Check className="size-3" />
            )}
          </span>
          <span className="truncate font-medium">{rule.rule_name}</span>
          <ChevronDown
            className={cn(
              'ml-auto size-3 text-muted-foreground transition-transform sm:hidden',
              open && 'rotate-180',
            )}
          />
        </span>
        <span
          className={cn(
            'w-fit rounded-full border px-2 py-0.5 text-[10px]',
            informational
              ? 'border-sky-400/20 bg-sky-400/10 text-sky-300'
              : matched
                ? 'border-rose-400/20 bg-rose-400/10 text-rose-300'
                : 'border-lime-400/20 bg-lime-400/10 text-lime-300',
          )}
        >
          {informational ? 'INFO' : matched ? 'TRIGGERED' : 'PASS'}
        </span>
        <span className="truncate text-[11px] text-muted-foreground">
          {rule.action_type || 'No action'}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {Object.keys(rule.signal_values).length} values{' '}
          <ChevronDown className={cn('ml-1 hidden size-3 sm:inline', open && 'rotate-180')} />
        </span>
      </button>
      {open && (
        <div className="space-y-1 border-t border-white/[0.06] px-10 py-3 text-[11px] text-muted-foreground">
          <p>{rule.description || rule.condition_summary || 'No rule description returned.'}</p>
          {rule.match_reason && (
            <p>
              <span className="text-zinc-300">Reason:</span> {rule.match_reason}
            </p>
          )}
          {rule.linked_policies.length > 0 && (
            <p>
              <span className="text-zinc-300">Policies:</span> {rule.linked_policies.join(', ')}
            </p>
          )}
          {Object.keys(rule.signal_values).length > 0 && (
            <p>
              <span className="text-zinc-300">Signal values:</span>{' '}
              {Object.entries(rule.signal_values)
                .map(([key, value]) => `${key}=${value.toFixed(3)}`)
                .join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ActionsPanel({
  actions,
  previews,
}: {
  actions: EnforcementResult[];
  previews: EnforcementPreview[];
}) {
  return (
    <OutputSection
      title="Enforcement actions"
      detail={`${actions.length} executed · ${previews.length} proposed`}
      icon={<ShieldCheck className="size-4" />}
    >
      <div className="space-y-2">
        {actions.length === 0 && previews.length === 0 ? (
          <EmptyDetail label="No enforcement actions or proposed actions returned." />
        ) : (
          <>
            {actions.map((action, index) => (
              <div
                key={`executed-${action.action}-${index}`}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2.5"
              >
                <span className="flex size-7 items-center justify-center rounded-lg bg-violet-500/10 text-violet-300">
                  <ShieldCheck className="size-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-zinc-200">{action.action}</p>
                  <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                    {action.reason || 'No reason returned'}
                  </p>
                </div>
                {action.verdict && (
                  <StatusChip
                    label={formatActionVerdictLabel(action.verdict)}
                    className={verdictClass(action.verdict)}
                  />
                )}
                {action.executed_at && (
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(action.executed_at).toLocaleString()}
                  </span>
                )}
              </div>
            ))}
            {previews.map((preview, index) => (
              <div
                key={`proposed-${preview.action}-${index}`}
                className="rounded-lg border border-amber-400/20 bg-amber-400/[0.04] px-3 py-2.5"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex size-7 items-center justify-center rounded-lg bg-amber-500/10 text-amber-300">
                    <ShieldAlert className="size-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-zinc-200">Proposed: {preview.action}</p>
                    <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                      {preview.reason || 'Rule matched; no execution recorded'}
                    </p>
                  </div>
                  {preview.verdict && (
                    <StatusChip
                      label={formatActionVerdictLabel(preview.verdict)}
                      className={verdictClass(preview.verdict)}
                    />
                  )}
                </div>
                {preview.would_execute.length > 0 && (
                  <p className="mt-2 pl-10 text-[10px] text-amber-200/60">
                    Would execute: {preview.would_execute.join(' · ')}
                  </p>
                )}
                {preview.blocked_by && (
                  <p className="mt-1 pl-10 text-[10px] text-muted-foreground">
                    Not selected: {preview.blocked_by}
                  </p>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </OutputSection>
  );
}

function RawPayloadPanel({ payload }: { payload: Record<string, unknown> | undefined }) {
  return (
    <details className="group rounded-lg border border-white/[0.08] bg-[#0d0d10]">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-xs text-muted-foreground">
        <Code2 className="size-3.5 text-violet-400" /> Raw Coop response{' '}
        <ChevronDown className="ml-auto size-3.5 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <pre className="max-h-80 overflow-auto border-t border-white/[0.08] p-3 font-mono text-[10px] leading-relaxed text-muted-foreground">
        {payload ? JSON.stringify(payload, null, 2) : 'No raw response captured.'}
      </pre>
    </details>
  );
}

function OutputSection({
  title,
  detail,
  icon,
  children,
}: {
  title: string;
  detail: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-white/[0.08] bg-white/[0.015] p-3.5 sm:p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-violet-400">{icon}</span>
          <div>
            <h3 className="text-xs font-semibold text-white">{title}</h3>
            <p className="mt-0.5 text-[10px] text-muted-foreground">{detail}</p>
          </div>
        </div>
      </div>
      {children}
    </section>
  );
}

function EmptyDetail({ label }: { label: string }) {
  return <p className="px-3 py-5 text-center text-[11px] text-muted-foreground">{label}</p>;
}

function StageIcon({ status }: { status: string }) {
  if (status === 'completed')
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-lime-400/10 text-lime-300">
        <Check className="size-3.5" />
      </span>
    );
  if (status === 'failed')
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-rose-400/10 text-rose-300">
        <CircleX className="size-3.5" />
      </span>
    );
  if (status === 'running')
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-sky-400/10 text-sky-300">
        <Loader2 className="size-3.5 animate-spin" />
      </span>
    );
  return (
    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-muted-foreground">
      <CircleDashed className="size-3.5" />
    </span>
  );
}

function PanelHeader({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-white/[0.08] px-4 py-3 sm:px-5">
      <span className="text-violet-400">{icon}</span>
      <div>
        <h2 className="text-sm font-semibold text-white">{title}</h2>
        <p className="mt-0.5 text-[11px] text-muted-foreground">{detail}</p>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="mt-3 block space-y-1.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function DarkSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="flex h-9 w-full rounded-md border border-white/10 bg-[#19191d] px-2 text-xs text-zinc-200 outline-none focus:border-violet-400/60"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function StatusChip({ label, className }: { label: string; className: string }) {
  return (
    <span
      className={cn('shrink-0 rounded-full border px-2 py-1 text-[10px] font-medium', className)}
    >
      {label}
    </span>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.025] p-2.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold text-zinc-200">{value.toLocaleString()}</p>
    </div>
  );
}

function HistoryPanel({
  history,
  onClear,
  onOpen,
  onLoad,
  loadingPostId,
  onRetry,
}: {
  history: TestHistoryEntry[];
  onClear: () => void;
  onOpen: (postId: number) => void;
  onLoad: (postId: number) => Promise<void>;
  loadingPostId: number | null;
  onRetry: (postId: number) => Promise<void>;
}) {
  if (history.length === 0) return null;
  return (
    <section className={cn(panelClass, 'overflow-hidden')}>
      <div className="flex items-center justify-between border-b border-white/[0.08] px-4 py-3">
        <div className="flex items-center gap-2">
          <Clock3 className="size-4 text-violet-400" />
          <div>
            <h2 className="text-sm font-semibold text-white">Recent test runs</h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Local browser history</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-[10px] text-muted-foreground hover:text-zinc-300"
        >
          Clear
        </button>
      </div>
      <div className="divide-y divide-white/[0.06]">
        {history.slice(0, 8).map((entry) => (
          <div key={entry.postId} className="flex items-center gap-3 px-4 py-2.5">
            <button
              type="button"
              onClick={() => onOpen(entry.postId)}
              className="min-w-0 flex-1 text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-zinc-300">#{entry.postId}</span>
                <span className="truncate text-[11px] text-muted-foreground">{entry.fileName}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-[10px] text-muted-foreground">
                <span>{entry.fileType}</span>
                {entry.fileSize > 0 && <span>{formatFileSize(entry.fileSize)}</span>}
                <span>{new Date(entry.createdAt).toLocaleDateString()}</span>
                <span className="text-amber-400/70">
                  {publicationStatusLabel(entry.publicationStatus, entry.pipelineOnly)}
                </span>
              </div>
            </button>
            <div className="flex shrink-0 items-center gap-1.5">
              {entry.verdict ? (
                <div className="flex flex-col items-end gap-0.5">
                  <StatusChip
                    label={displayHarnessVerdictLabel(entry.verdict, isHistoryVerdictFinal(entry))}
                    className={cn(
                      verdictClass(entry.verdict, !isHistoryVerdictFinal(entry)),
                      !isHistoryVerdictFinal(entry) && 'border-dashed opacity-90',
                    )}
                  />
                  {!isHistoryVerdictFinal(entry) && (
                    <span className="text-[9px] uppercase tracking-wide text-sky-400/80">
                      {loadingPostId === entry.postId ? 'auto-refreshing' : 'provisional'}
                    </span>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => void onRetry(entry.postId)}
                  className="rounded border border-white/10 px-2 py-1 text-[10px] text-muted-foreground hover:bg-white/[0.05] hover:text-zinc-200"
                >
                  Retry
                </button>
              )}
              <button
                type="button"
                onClick={() => void onLoad(entry.postId)}
                disabled={loadingPostId === entry.postId}
                title="Auto-refresh from Coop until final"
                className="inline-flex items-center gap-1 rounded border border-violet-400/20 bg-violet-400/[0.05] px-2 py-1 text-[10px] text-violet-300 hover:bg-violet-400/10 disabled:cursor-wait disabled:opacity-60"
              >
                {loadingPostId === entry.postId ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : (
                  <Database className="size-3" />
                )}
                <span>{loadingPostId === entry.postId ? 'Refreshing' : 'Load'}</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
